import { type FC, useEffect, useMemo, useState } from "react";

import type { QuotaForecastingFilters } from "../../types/quotaForecasting";

import {
  normalizeTextFilter,
  parseLocalDateTimeInputValue,
  toLocalDateTimeInputValue,
} from "../../utils/dateFilters";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { FormInput } from "../common/forms/FormInput";
import { Text } from "../common/Text";

export interface QuotaForecastingFiltersPanelProps {
  filters: Required<
    Pick<QuotaForecastingFilters, "fromTimestampMs" | "toTimestampMs">
  > &
    Omit<QuotaForecastingFilters, "fromTimestampMs" | "toTimestampMs">;
  isLoading: boolean;
  onSetFilters: (patch: QuotaForecastingFilters) => void;
  onSetDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  onReset: () => void;
  onRefresh: () => void;
}

const QUICK_RANGES = [
  { durationMs: 60 * 60 * 1000, label: "1H" },
  { durationMs: 6 * 60 * 60 * 1000, label: "6H" },
  { durationMs: 24 * 60 * 60 * 1000, label: "24H" },
  { durationMs: 7 * 24 * 60 * 60 * 1000, label: "7D" },
] as const;

export const QuotaForecastingFiltersPanel: FC<
  QuotaForecastingFiltersPanelProps
> = ({
  filters,
  isLoading,
  onSetDateRange,
  onSetFilters,
  onReset,
  onRefresh,
}) => {
  const [fromInput, setFromInput] = useState(() =>
    toLocalDateTimeInputValue(filters.fromTimestampMs),
  );
  const [toInput, setToInput] = useState(() =>
    toLocalDateTimeInputValue(filters.toTimestampMs),
  );

  useEffect(() => {
    setFromInput(toLocalDateTimeInputValue(filters.fromTimestampMs));
  }, [filters.fromTimestampMs]);

  useEffect(() => {
    setToInput(toLocalDateTimeInputValue(filters.toTimestampMs));
  }, [filters.toTimestampMs]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.provider) {
      count += 1;
    }
    if (filters.model) {
      count += 1;
    }
    return count;
  }, [filters.model, filters.provider]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Text variant="label">Range</Text>
          {QUICK_RANGES.map((range) => (
            <Button
              key={range.label}
              variant="outline"
              size="sm"
              onClick={() => {
                const to = Date.now();
                const from = Math.max(0, to - range.durationMs);
                onSetDateRange(from, to);
              }}
            >
              {range.label}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {activeFilterCount > 0 && (
            <Badge variant="info" size="sm">
              {activeFilterCount} active
            </Badge>
          )}
          {isLoading && (
            <Badge variant="info" size="md" className="animate-pulse">
              Updating…
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={onReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <FormInput
          type="datetime-local"
          label="From"
          value={fromInput}
          onChange={(event) => {
            const next = event.target.value;
            setFromInput(next);
            const parsed = parseLocalDateTimeInputValue(next);
            if (parsed === null) {
              return;
            }
            onSetDateRange(parsed, Math.max(parsed, filters.toTimestampMs));
          }}
        />
        <FormInput
          type="datetime-local"
          label="To"
          value={toInput}
          onChange={(event) => {
            const next = event.target.value;
            setToInput(next);
            const parsed = parseLocalDateTimeInputValue(next);
            if (parsed === null) {
              return;
            }
            onSetDateRange(Math.min(filters.fromTimestampMs, parsed), parsed);
          }}
        />
        <FormInput
          label="Provider"
          placeholder="bedrock"
          value={filters.provider ?? ""}
          onChange={(event) =>
            onSetFilters({ provider: normalizeTextFilter(event.target.value) })
          }
        />
        <FormInput
          label="Model"
          placeholder="bedrock/global.anthropic.claude-sonnet-4-6"
          value={filters.model ?? ""}
          onChange={(event) =>
            onSetFilters({ model: normalizeTextFilter(event.target.value) })
          }
        />
      </div>
    </div>
  );
};
